import { SideNav } from '@servicetitan/design-system';
import useBrowserState from '@modules/BrowserModule/useBrowserState';
import { FeatureFlagKeys, useFeatureFlags } from '@modules/FeatureFlagModule';
import {
  useAudioDevices,
  deriveEffectiveErrorCategory,
} from '@modules/SoftPhoneModule';
import { mainLayoutLocators } from '@locators/main-layout';
import { FC, useMemo } from 'react';
import AudioDeviceErrorBadge from '../../assets/audio-device-error-badge.svg';
import styles from './AudioSettingsNavItem.module.css';

export interface AudioSettingsNavItemProps {
  onOpen: () => void;
}

/**
 * Sidebar nav entry for Audio Settings. Renders nothing when the feature flag is off.
 * Shows an error badge when audio devices are not healthy: either the softphone reported an
 * error category, or the browser denied microphone access (aligned with {@link AudioDevicePanel}).
 */
export const AudioSettingsNavItem: FC<AudioSettingsNavItemProps> = ({
  onOpen,
}) => {
  const {
    [FeatureFlagKeys.featureAudioDeviceManagement]:
      featureAudioDeviceManagement,
  } = useFeatureFlags();
  const { errorCategory, isDevicesReady } = useAudioDevices();
  const { microphoneAccess } = useBrowserState();

  const showErrorBadge = useMemo(
    () =>
      deriveEffectiveErrorCategory(
        errorCategory,
        isDevicesReady,
        microphoneAccess,
      ) !== null,
    [errorCategory, isDevicesReady, microphoneAccess],
  );

  if (!featureAudioDeviceManagement) {
    return null;
  }

  return (
    <SideNav.Item
      id={mainLayoutLocators.sidenav.audioSettingsNavBtn}
      onClick={onOpen}
    >
      Audio Settings
      {showErrorBadge && (
        <AudioDeviceErrorBadge
          id={mainLayoutLocators.sidenav.audioSettingsErrorBadge}
          className={styles.errorDot}
          aria-label='audio device error'
        />
      )}
    </SideNav.Item>
  );
};

export default AudioSettingsNavItem;
